import { useNavigation } from '@react-navigation/native';
import React, { useState } from 'react';
import { StyleSheet, TouchableOpacity } from 'react-native';

import ProfileCard from '@components/Profile/ProfileCard';
import { Box, Text } from '@components/Restyle';
import Button from '@components/shared/Button/Button';
import Icon from '@components/shared/Icon/Icon';
import { ProfileCardSkeleton } from '@components/shared/skeletons';
import { useApolloClient } from '@hooks/useApolloClient';
import { useGoogleAuth } from '@hooks/useGoogleAuth';
import useMe from '@hooks/useMe';
import { useMinimumVisible } from '@hooks/useMinimumVisible';
import { useNotification } from '@hooks/useNotification';
import { MIXPANEL_EVENTS } from '@src/constants/constants';
import { useAuthContext } from '@src/context/authContext';
import { useMixpanel } from '@src/utils/mixpanel';
import { AsyncStorage } from '@utils/storage';

// Mobile-browser profile screen (web). Native uses @screens/Profile/MyProfile.tsx.
export const MyProfile: React.FC = () => {
  const navigation = useNavigation<any>();
  const { me, loading: meLoading } = useMe();
  const { removeToken: removeAuthToken } = useAuthContext();
  const { deleteToken: deleteNotificationToken } = useNotification();
  const { clearUserInfo } = useGoogleAuth();
  const { trackEvent } = useMixpanel();
  const { client } = useApolloClient();
  const [loading, setLoading] = useState(false);
  // Never show a blank region while `me` is still loading (TA-10277).
  const showProfileSkeleton = useMinimumVisible(!me && !!meLoading);

  const goBack = () => {
    // This screen lives inside the my-account ProfileStack, which was pushed on
    // top of projects-stack (where ProjectWelcomeMobileScreen renders). Pop the
    // ROOT entry to return to the project, not the ProfileStack's own history
    // (which would land on the MyAccount "tabs" screen instead).
    const parent = navigation.getParent();
    if (parent?.canGoBack()) {
      parent.goBack();
    } else if (navigation.canGoBack()) {
      navigation.goBack();
    } else {
      navigation.navigate('projects-stack');
    }
  };

  const logout = async () => {
    if (loading) return; // guard against double-taps
    setLoading(true);
    try {
      clearUserInfo && clearUserInfo();
      window.sessionStorage.setItem('screenInfo-activeChat', '');
      if (me?.id) {
        globalThis.localStorage?.removeItem(
          `checklist_onboarding_tooltip_dismissed_${me.id}`
        );
      }
      await deleteNotificationToken();
      await AsyncStorage.clear();
    } catch {
      // empty — best-effort cleanup; always fall through to finally so a
      // transient notification/storage failure can't block the actual
      // logout (matches MyAccount.web.tsx's logout()).
    } finally {
      // Deliberately not resetting `loading` here: this block always
      // completes the logout regardless of earlier failures above, so the
      // button should stay locked until the auth swap unmounts this screen
      // rather than briefly re-enabling and defeating the double-tap guard.
      trackEvent(MIXPANEL_EVENTS.USER_LOGGED_OUT);
      removeAuthToken();
      try {
        await client?.clearStore();
      } catch {
        // empty — best-effort; always still stop the client below.
      } finally {
        client?.stop();
      }
    }
  };

  return (
    <Box
      flex={1}
      backgroundColor='white'
      testID='my-profile-web'
      style={styles.container}>
      {/* Header */}
      <Box
        flexDirection='row'
        alignItems='center'
        justifyContent='space-between'
        paddingHorizontal='m'
        paddingVertical='s'>
        <TouchableOpacity onPress={goBack} accessibilityLabel='Go back'>
          <Icon name='ChevronLeft' width={24} height={24} color='textPrimary' />
        </TouchableOpacity>
        <Text variant='labelLarge' color='textPrimary'>
          Profile
        </Text>
        <Box style={styles.headerSpacer} />
      </Box>

      {/* Profile details. Gate on showProfileSkeleton alone — checking `me`
          first would short-circuit the moment it resolves, bypassing
          useMinimumVisible's 150ms hold entirely. */}
      {showProfileSkeleton ? (
        <Box margin='m'>
          <ProfileCardSkeleton />
        </Box>
      ) : (
        me && <ProfileCard user={me} isMe />
      )}

      {/* Log Out (fixed at bottom) */}
      <Box margin='m'>
        <Button
          onPress={logout}
          isLoading={loading}
          variant='logout'
          btnTextColor='alertRed'
          accessibilityLabel='Log Out'>
          <Text variant='buttonLabel' color='alertRed'>
            Log out
          </Text>
        </Button>
      </Box>
    </Box>
  );
};

const styles = StyleSheet.create({
  // Clamp to the viewport so header + logout stay fixed and content doesn't overflow.
  container: {
    height: '100%' as any,
    overflow: 'hidden' as any,
  },
  headerSpacer: {
    width: 24,
  },
});

export default MyProfile;
